// The run drawn as a flowchart on a gridded canvas — a PROVENANCE picture of a
// FINISHED run, not a live one.
//
// It used to animate: marching dashes on the wire the work was flowing down, a
// breathing ring around the running card, the canvas scrolling itself to follow
// the active node. That is gone. Progress now lives as a compact strip in the
// chat thread; this canvas is opened afterwards, from the RESULT, to answer
// "how did that output get here?" — and animating a finished run as though it
// were still happening is a picture of something that is not occurring.
//
// The engine (lib/agentRun.js) emits a GRAPH, not a list: every node carries
// `dependsOn: [id, …]`, and two nodes naming the same dependencies genuinely
// ran together. That is drawn as what it is — a fork into two lanes and a join
// back into one. There is deliberately NO "parallel" badge or dashed band any
// more: two nodes sharing a row IS the signal, and labelling it was explaining
// the joke.
//
// NODES ARE RECTANGLES: icon, title, a small status mark, nothing else. The
// headline, detail lines, metric, timing and per-lead verdict all live in the
// details panel AgentRunView renders to the right of this canvas, shown for the
// SELECTED node.
//
// PURELY PRESENTATIONAL. Props in, one callback out. It never imports
// agentRun.js, never starts anything, and holds no run state.
//
// ACCESSIBILITY. The nodes are real <button>s now, so the canvas is no longer
// aria-hidden — it could not be, since selecting a node is the whole
// interaction. DOM order is emission order (Start, every pass in order, End),
// which is a sane reading order, and each button carries a label spelling out
// its position, status and — in lead mode — that lead's verdict. Arrow keys
// walk the chain, Home/End jump to its ends.
//
// ── the two traps this file is written around ─────────────────────────────
//  (i) `border border-line` computes to `0px none` in this project. Preflight
//      is off (tailwind.config.js), so Tailwind's border-WIDTH utility ships no
//      border-STYLE and nothing draws. Every border below is the arbitrary
//      property form: [border:1px_solid_var(--border)].
// (ii) Two box-shadow utilities on one element do not merge — the later one in
//      the stylesheet wins and the other silently vanishes. The selected node's
//      ring is therefore folded into ONE shadow value.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIcon,
  CampaignsIcon,
  CheckIcon,
  CheckSquareIcon,
  ClockIcon,
  CloseIcon,
  CloudIcon,
  DealFlowIcon,
  FilterIcon,
  LeadQualityIcon,
  MailIcon,
  PhoneIcon,
  SearchIcon,
  SparklesIcon,
  TeamIcon,
  UserIcon,
} from './icons.jsx'

// ── geometry ────────────────────────────────────────────────────────────────
// Every node is the SAME size, and now a compact rectangle rather than a card,
// because it no longer carries content that could grow. Fixed size is what lets
// the edges be drawn without measuring anything: positions are a pure function
// of the graph shape.
export const CARD_W = 228
export const CARD_H = 58
const PILL_H = 30
// The End pill's caption is the run's verdict ("End · stopped, nothing sent"),
// so a fixed pill width truncated the very thing it exists to say. Width is
// estimated from the caption instead — deliberately generous, because the text
// is centred, so an over-estimate costs a little air and an under-estimate
// costs an ellipsis.
const pillWidth = (label) =>
  Math.max(112, Math.min(300, Math.round(String(label || '').length * 7.4) + 46))
const GAP_MAIN = 42 // between depth bands (along the flow)
const GAP_CROSS = 26 // between parallel siblings (across the flow)
const PAD = 26 // canvas breathing room
const STACK_BOW = 34 // how far a row-skipping edge swings out beside a stack

// NOTE: every px value here is a literal bracket value at its call site.
// Tailwind's JIT scans source text for class names, so `w-[${X}px]` produces a
// class that is never generated.

/**
 * Lay the graph out. PURE and deterministic: same nodes in, same numbers out,
 * no DOM reads, no measurement loop, no randomness.
 *
 * Columns come from dependency DEPTH (the longest path to a node, so a node
 * never sits left of something it waits on) and rows from `lane` — the engine's
 * own layout hint for parallel siblings — falling back to emission order. Bands
 * are centred on the cross axis, which is what makes a fork look like a fork:
 * two siblings straddle the centre line their shared parent sits on, and their
 * shared child pulls back to it.
 *
 * `orientation: 'horizontal'` puts depth on x and lanes on y; 'vertical' swaps
 * them. Same maths, one transposition at the end.
 *
 * Start and End are laid out as real nodes (kind 'pill') so the flow has proper
 * bookends wired with proper edges.
 */
export function layoutFlow(passes, { orientation = 'vertical', startLabel = 'Start', endLabel = 'End' } = {}) {
  const real = Array.isArray(passes) ? passes : []
  const empty = { orientation, width: 0, height: 0, nodes: [], edges: [] }
  if (!real.length) return empty

  const known = new Set(real.map((p) => p.id))
  // A node nobody depends on is a leaf, and every leaf feeds the End pill.
  const referenced = new Set()
  real.forEach((p) => (p.dependsOn || []).forEach((d) => known.has(d) && referenced.add(d)))

  const items = [{ id: '__start__', kind: 'pill', dependsOn: [], lane: null, order: -1 }]
  real.forEach((p, i) => {
    // Self-references and dangling ids are dropped rather than trusted; a root
    // is re-parented onto Start so nothing floats.
    const deps = (p.dependsOn || []).filter((d) => known.has(d) && d !== p.id)
    items.push({
      ...p,
      kind: 'node',
      order: i,
      lane: typeof p.lane === 'number' ? p.lane : null,
      dependsOn: deps.length ? deps : ['__start__'],
    })
  })
  const leaves = real.filter((p) => !referenced.has(p.id)).map((p) => p.id)
  items.push({
    id: '__end__',
    kind: 'pill',
    dependsOn: leaves.length ? leaves : ['__start__'],
    lane: null,
    order: real.length,
  })

  const at = new Map(items.map((it, i) => [it.id, i]))

  // Longest path from a root. Memoised, with a visiting flag so a malformed
  // graph degrades to a drawable layout instead of blowing the stack.
  const depth = new Array(items.length).fill(0)
  const mark = new Array(items.length).fill(0)
  const depthOf = (i) => {
    if (mark[i]) return depth[i]
    mark[i] = 1
    let d = 0
    for (const dep of items[i].dependsOn) {
      const j = at.get(dep)
      if (j === undefined || j === i) continue
      if (mark[j] === 1) continue // cycle: ignore the back edge
      d = Math.max(d, depthOf(j) + 1)
    }
    depth[i] = d
    mark[i] = 2
    return d
  }
  items.forEach((_, i) => depthOf(i))

  const bandIds = []
  items.forEach((_, i) => {
    ;(bandIds[depth[i]] || (bandIds[depth[i]] = [])).push(i)
  })
  // Laned siblings first in lane order; everything else keeps emission order.
  bandIds.forEach((band) =>
    band.sort((a, b) => {
      const la = items[a].lane
      const lb = items[b].lane
      if (la !== lb) return (la ?? 1e6) - (lb ?? 1e6)
      return items[a].order - items[b].order
    }),
  )

  // 'stack': ONE node per band, top to bottom — the narrowest the graph can
  // be drawn (one card wide). Fork siblings, which the other orientations put
  // side by side, become consecutive rows; the edges below bow out sideways
  // for the rows they skip, so the fork is still visible as a fork.
  const stacked = orientation === 'stack'
  const bands = stacked ? bandIds.flatMap((band) => band.map((i) => [i])) : bandIds
  const bandOf = new Array(items.length).fill(0)
  bands.forEach((band, b) => band.forEach((i) => (bandOf[i] = b)))

  const sizeOf = (i) =>
    items[i].kind === 'pill'
      ? { w: pillWidth(items[i].id === '__start__' ? startLabel : endLabel), h: PILL_H }
      : { w: CARD_W, h: CARD_H }
  const vertical = orientation !== 'horizontal'
  const mainOf = (i) => (vertical ? sizeOf(i).h : sizeOf(i).w)
  const crossOf = (i) => (vertical ? sizeOf(i).w : sizeOf(i).h)

  const bandCross = bands.map(
    (band) => band.reduce((a, i) => a + crossOf(i), 0) + GAP_CROSS * (band.length - 1),
  )
  // A stacked graph needs room beside the single column for the bowed edges.
  const canvasCross = Math.max(...bandCross) + (stacked ? STACK_BOW * 2 * 2 : 0)

  const pos = new Array(items.length)
  let mainCursor = PAD
  bands.forEach((band, d) => {
    const bandMain = Math.max(...band.map(mainOf))
    let cross = PAD + (canvasCross - bandCross[d]) / 2
    band.forEach((i) => {
      const main = mainCursor + (bandMain - mainOf(i)) / 2
      pos[i] = vertical ? { x: cross, y: main } : { x: main, y: cross }
      cross += crossOf(i) + GAP_CROSS
    })
    mainCursor += bandMain + GAP_MAIN
  })
  const canvasMain = mainCursor - GAP_MAIN + PAD

  const nodes = items.map((it, i) => ({
    ...it,
    depth: depth[i],
    x: pos[i].x,
    y: pos[i].y,
    w: sizeOf(i).w,
    h: sizeOf(i).h,
  }))

  // One edge per dependency, anchored on the faces the flow crosses. The curve
  // is a cubic whose handles run along the flow axis, so edges leave a node
  // going the way the work went and arrive pointing at the next one.
  const edges = []
  nodes.forEach((target, i) => {
    items[i].dependsOn.forEach((depId) => {
      const j = at.get(depId)
      if (j === undefined || j === i) return
      const source = nodes[j]
      const from = vertical
        ? { x: source.x + source.w / 2, y: source.y + source.h }
        : { x: source.x + source.w, y: source.y + source.h / 2 }
      const to = vertical
        ? { x: target.x + target.w / 2, y: target.y }
        : { x: target.x, y: target.y + target.h / 2 }
      const span = vertical ? to.y - from.y : to.x - from.x
      const k = Math.max(16, Math.min(64, Math.abs(span) * 0.55))
      // In a stack, an edge that skips rows bows out to the side (alternating
      // left / right by how many it skips) so it does not run through the
      // cards in between; an adjacent-row edge is the plain vertical curve.
      // A cubic with both handles offset by `bow` peaks at about 0.75 × bow, so
      // the handle is sized from the clearance wanted, not set to it: the curve
      // has to pass OUTSIDE the cards it skips (half a card plus a margin),
      // swinging a little wider the more rows it skips.
      const skipped = stacked ? bandOf[i] - bandOf[j] - 1 : 0
      const bow =
        skipped > 0
          ? ((skipped % 2 ? -1 : 1) * (CARD_W / 2 + STACK_BOW * Math.min(2, skipped))) / 0.75
          : 0
      const d = vertical
        ? bow
          ? `M ${from.x} ${from.y} C ${from.x + bow} ${from.y + k} ${to.x + bow} ${to.y - k} ${to.x} ${to.y}`
          : `M ${from.x} ${from.y} C ${from.x} ${from.y + k} ${to.x} ${to.y - k} ${to.x} ${to.y}`
        : `M ${from.x} ${from.y} C ${from.x + k} ${from.y} ${to.x - k} ${to.y} ${to.x} ${to.y}`
      const arrow = vertical
        ? `${to.x - 4.5},${to.y - 7.5} ${to.x + 4.5},${to.y - 7.5} ${to.x},${to.y - 0.5}`
        : `${to.x - 7.5},${to.y - 4.5} ${to.x - 7.5},${to.y + 4.5} ${to.x - 0.5},${to.y}`
      edges.push({ id: `${depId}->${target.id}`, source: depId, target: target.id, d, arrow })
    })
  })

  // canvasMain already carries the leading and trailing PAD (the cursor started
  // at PAD and one PAD is added after the last band); the cross axis only ever
  // measured its content, so it gets both here.
  return {
    orientation: stacked ? 'stack' : vertical ? 'vertical' : 'horizontal',
    width: vertical ? canvasCross + PAD * 2 : canvasMain,
    height: vertical ? canvasMain : canvasCross + PAD * 2,
    nodes,
    edges,
  }
}

// ── presentation tables ─────────────────────────────────────────────────────

// Keyed by the engine's pass id, generic sparkle for anything unknown, so
// agentRun.js can add a node without this file having to know first.
const PASS_ICON = {
  source: SearchIcon,
  reachable: MailIcon,
  outreach: ActivityIcon,
  contacted: FilterIcon,
  'hs-match': CloudIcon,
  'hs-deals': DealFlowIcon,
  'hs-tasks': CheckSquareIcon,
  'hs-screen': LeadQualityIcon,
  enriched: SparklesIcon,
  audience: TeamIcon,
  campaign: CampaignsIcon,
  // The draft-provenance pipeline (lib/draftProvenance.js) — the EVIDENCE
  // behind ONE lead's email, distinct from how the lead was selected above.
  'ev-row': LeadQualityIcon,
  'ev-history': FilterIcon,
  'ev-crm': CloudIcon,
  'ev-research': SearchIcon,
  'ev-draft': MailIcon,
  // Retired node ids, kept so a receipt rebuilt by older code still draws.
  'draft-due': CheckSquareIcon,
  'draft-score': LeadQualityIcon,
  'draft-research': SearchIcon,
  'draft-verify': UserIcon,
  'draft-cadence': CampaignsIcon,
  'draft-opener': SparklesIcon,
  'draft-write': MailIcon,
  'draft-humanize': FilterIcon,
  // The follow-up pipelines (buildFollowupDraftPasses / buildCallPasses in
  // lib/draftProvenance.js) — the wait gate, the reply freeze, the rotated
  // hook, and — on call touches — the talking points Bo dials with.
  'fu-thread': MailIcon,
  'fu-clock': ClockIcon,
  'fu-freeze': ActivityIcon,
  'fu-excl': CloseIcon,
  'fu-position': CampaignsIcon,
  'fu-row': LeadQualityIcon,
  'fu-draft': SparklesIcon,
  // Retired follow-up ids, same reason as above.
  'fu-due': ClockIcon,
  'fu-cadence': CampaignsIcon,
  'fu-research': SearchIcon,
  'fu-hook': SparklesIcon,
  'fu-write': MailIcon,
  'fu-humanize': FilterIcon,
  // The call chain.
  'call-thread': MailIcon,
  'call-clock': ClockIcon,
  'call-checks': ActivityIcon,
  'call-row': LeadQualityIcon,
  'call-points': PhoneIcon,
}

export const passIcon = (id) => PASS_ICON[id] || SparklesIcon

// Skipped is quiet-but-different (dashed, inset) rather than red: skipping the
// HubSpot branch because the CRM is unconfigured is a HEALTHY outcome, and an
// alarming node would teach people to distrust a run that behaved correctly.
const NODE = {
  pending: '[border:1px_solid_var(--border)] bg-surface shadow-card',
  running: '[border:1px_solid_var(--border)] bg-surface shadow-card',
  done: '[border:1px_solid_var(--border)] bg-surface shadow-card',
  skipped: '[border:1px_dashed_var(--border)] bg-panel shadow-card',
  failed: '[border:1px_solid_#dc2626] bg-[#fef2f2] shadow-card dark:bg-[rgba(220,38,38,0.12)]',
}

// The selected node. ONE box-shadow value, ring folded in — a second shadow
// utility would silently replace this one (trap ii).
const NODE_SELECTED =
  '[border:1px_solid_var(--primary)] bg-primary-tint ' +
  'shadow-[0_1px_3px_rgba(15,23,42,0.10),0_0_0_3px_rgba(60,135,247,0.22)]'

const TILE = {
  pending: 'bg-panel text-faint',
  running: 'bg-primary-tint text-primary',
  done: 'bg-primary-tint text-primary',
  skipped: '[border:1px_dashed_var(--border)] bg-panel text-faint',
  failed: 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(220,38,38,0.2)] dark:text-[#fca5a5]',
}

export const BADGE_TONE = {
  pending: 'bg-panel text-dim',
  running: 'bg-primary-tint text-primary-dark',
  done: 'bg-primary-tint text-primary-dark',
  skipped: 'bg-panel text-dim',
  failed: 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(220,38,38,0.2)] dark:text-[#fca5a5]',
}

const TITLE = {
  pending: 'text-dim',
  running: 'text-ink',
  done: 'text-ink',
  skipped: 'text-dim',
  failed: 'text-[#b91c1c] dark:text-[#fca5a5]',
}

export const BADGE_TEXT = {
  pending: 'Never ran',
  running: 'Interrupted',
  done: 'Done',
  skipped: 'Skipped',
  failed: 'Failed',
}

export function elapsedLabel(pass) {
  const ms = (pass?.endedAt || 0) - (pass?.startedAt || 0)
  if (!pass?.startedAt || !pass?.endedAt || ms <= 0) return ''
  return ms < 950 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`
}

/**
 * Shape, not colour — status is never carried by hue alone. Exported because
 * the details panel shows the same mark beside the same word, and two marks
 * that disagree would be worse than none.
 */
export function StatusMark({ status, className = '' }) {
  const cls = className || 'h-[10px] w-[10px]'
  if (status === 'done') return <CheckIcon className={cls} />
  if (status === 'failed') return <CloseIcon className={cls} />
  if (status === 'skipped') return <span className="h-[2px] w-[9px] rounded-full bg-faint" />
  return <span className="h-[5px] w-[5px] rounded-full [border:1px_solid_currentColor]" />
}

/**
 * Edge treatment for a SETTLED run. There is no "live" tone any more: nothing
 * is flowing, so nothing marches. The tone says what the wire CARRIED — work
 * (done), nothing (skipped/abandoned), or a stop (failed).
 */
function edgeTone(source, target) {
  const s = target?.status || 'pending'
  if (s === 'failed') return 'failed'
  if (target?.abandoned) return 'dead'
  if (s === 'skipped') return 'muted'
  if (s === 'done' || s === 'running') return 'done'
  const up = source?.status
  return up === 'done' || up === 'skipped' ? 'ready' : 'idle'
}

const EDGE_STROKE = {
  idle: 'var(--flow-edge)',
  ready: 'var(--flow-edge)',
  done: 'var(--primary)',
  muted: 'var(--flow-edge-muted)',
  dead: 'var(--flow-edge-muted)',
  failed: '#dc2626',
}
const EDGE_OPACITY = { idle: 0.5, ready: 0.6, done: 0.55, muted: 0.6, dead: 0.4, failed: 0.9 }

// ── the node rectangle ──────────────────────────────────────────────────────

function NodeRect({ node, selected, dimmed, label, onSelect, onKeyDown, buttonRef }) {
  const status = node.status || 'pending'
  const Icon = passIcon(node.id)

  return (
    <button
      type="button"
      ref={buttonRef}
      aria-pressed={selected}
      aria-label={label}
      title={node.title}
      onClick={() => onSelect(node.id)}
      onKeyDown={onKeyDown}
      className={`group flex h-full w-full cursor-pointer items-center gap-x-[11px] rounded-[12px] px-[13px] py-0 text-left transition-[opacity,background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.32,0.72,0.24,1)] focus-visible:[outline:2px_solid_var(--primary)] focus-visible:[outline-offset:3px] motion-reduce:transition-none ${
        selected ? NODE_SELECTED : NODE[status] || NODE.pending
      } ${dimmed ? 'opacity-35' : 'opacity-100'} ${
        selected ? '' : 'hover:[border-color:var(--primary)]'
      }`}
    >
      <span
        className={`grid h-[30px] w-[30px] flex-none place-items-center rounded-[9px] [&_svg]:h-[17px] [&_svg]:w-[17px] ${
          TILE[status] || TILE.pending
        }`}
      >
        <Icon aria-hidden="true" />
      </span>

      <span
        className={`line-clamp-2 min-w-0 flex-1 text-[12.5px] font-semibold leading-[1.3] ${
          selected ? 'text-primary-dark' : TITLE[status] || TITLE.pending
        }`}
      >
        {node.title}
      </span>

      <span
        aria-hidden="true"
        className={`grid h-[19px] w-[19px] flex-none place-items-center rounded-full ${
          BADGE_TONE[status] || BADGE_TONE.pending
        }`}
      >
        <StatusMark status={status} />
      </span>
    </button>
  )
}

function PillNode({ node, label, tone }) {
  return (
    <div
      aria-hidden="true"
      className={`flex h-full w-full items-center justify-center gap-x-2 rounded-full px-[11px] text-[11px] font-bold uppercase tracking-[0.05em] ${
        tone === 'live'
          ? '[border:1px_solid_var(--primary)] bg-primary-tint text-primary-dark'
          : '[border:1px_solid_var(--border)] bg-panel text-dim'
      }`}
    >
      <span
        className={`h-[7px] w-[7px] flex-none rounded-full ${
          tone === 'live' ? 'bg-primary' : node.id === '__start__' ? 'bg-primary' : 'bg-line'
        }`}
      />
      <span className="truncate">{label}</span>
    </div>
  )
}

// ── the canvas ──────────────────────────────────────────────────────────────

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReduced(mq.matches)
    sync()
    mq.addEventListener?.('change', sync)
    return () => mq.removeEventListener?.('change', sync)
  }, [])
  return reduced
}

/**
 * @param {object[]} passes         the run's nodes, each carrying dependsOn/lane
 * @param {string}   terminalLabel  what the End pill says
 * @param {boolean}  terminalDone   whether the run actually reached the end
 * @param {string}   selectedId     id of the node whose details are showing
 * @param {function} onSelect       (id) — '' clears the selection
 * @param {Set|null} reachedIds     when opened FOR ONE LEAD, the ids that lead
 *                                  actually reached (from the run's own trace).
 *                                  null means "not lead-scoped" — nothing dims.
 * @param {object}   verdicts       optional {id: verdictWord} for the labels
 * @param {boolean}  fit            scale the whole graph DOWN to the pane's
 *                                  width when it would otherwise scroll
 *                                  sideways (never below FIT_MIN, never up).
 *                                  For a slim column that still wants the
 *                                  whole picture — the draft provenance's
 *                                  Developer view, where the code panel takes
 *                                  the room.
 */
// The smallest the fitted graph may get: below this the labels stop being
// legible, and sideways scrolling is the honest fallback.
const FIT_MIN = 0.7

export default function FlowCanvas({
  passes = [],
  terminalLabel = 'End',
  terminalDone = false,
  selectedId = '',
  onSelect = () => {},
  reachedIds = null,
  verdicts = null,
  fit = false,
}) {
  const scrollRef = useRef(null)
  const btnRefs = useRef(new Map())
  const [viewport, setViewport] = useState({ w: 0, h: 0 })
  const reduced = usePrefersReducedMotion()

  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(() => setViewport({ w: el.clientWidth, h: el.clientHeight }))
    ro.observe(el)
    setViewport({ w: el.clientWidth, h: el.clientHeight })
    return () => ro.disconnect()
  }, [])

  // Flow left-to-right when the WHOLE pipeline fits without scrolling;
  // otherwise flow top-to-bottom, which is the axis this pane already scrolls
  // on and the one that stays legible in a narrow window. Both orientations
  // come out of the same pure layout function, so the fork is a fork either way
  // — siblings simply straddle the other axis.
  const layout = useMemo(() => {
    const wide = layoutFlow(passes, { orientation: 'horizontal', endLabel: terminalLabel })
    if (viewport.w > 0 && wide.width <= viewport.w) return wide
    const tall = layoutFlow(passes, { orientation: 'vertical', endLabel: terminalLabel })
    // A slim pane (`fit`) that cannot hold the fork side by side even at
    // FIT_MIN scale draws the graph as ONE column instead — every card at full
    // size, forks shown by their edges — rather than scaling the labels away
    // or scrolling sideways.
    if (fit && viewport.w > 0 && tall.width * FIT_MIN > viewport.w) {
      return layoutFlow(passes, { orientation: 'stack', endLabel: terminalLabel })
    }
    return tall
  }, [passes, viewport.w, terminalLabel, fit])

  // Scale-to-fit, when asked: the inner sheet keeps its true geometry (so the
  // pure layout and every edge stay untouched) and is transformed as a whole;
  // the wrapper takes the SCALED size so the scroll pane sizes to what is
  // seen. 1 whenever the graph fits or `fit` is off.
  const fitScale =
    fit && viewport.w > 0 && layout.width > viewport.w
      ? Math.max(FIT_MIN, viewport.w / layout.width)
      : 1

  const byId = useMemo(() => new Map(layout.nodes.map((n) => [n.id, n])), [layout])
  const order = useMemo(() => passes.map((p) => p.id), [passes])

  // Roving with real focus: arrows move focus AND selection, which is the whole
  // interaction of this view. Home/End jump to the ends of the chain.
  const move = useCallback(
    (from, delta) => {
      if (!order.length) return
      const i = order.indexOf(from)
      const next =
        delta === 'home'
          ? 0
          : delta === 'end'
            ? order.length - 1
            : Math.min(order.length - 1, Math.max(0, (i < 0 ? 0 : i) + delta))
      const id = order[next]
      onSelect(id)
      btnRefs.current.get(id)?.focus()
    },
    [order, onSelect],
  )

  const keyHandler = useCallback(
    (id) => (e) => {
      const vertical = layout.orientation !== 'horizontal'
      const forward = vertical ? 'ArrowDown' : 'ArrowRight'
      const back = vertical ? 'ArrowUp' : 'ArrowLeft'
      // Both axes work regardless of orientation: people press the arrow that
      // matches the picture, and the picture transposes with the window.
      if (e.key === forward || e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        move(id, 1)
      } else if (e.key === back || e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        move(id, -1)
      } else if (e.key === 'Home') {
        e.preventDefault()
        move(id, 'home')
      } else if (e.key === 'End') {
        e.preventDefault()
        move(id, 'end')
      } else if (e.key === 'Escape') {
        onSelect('')
      }
    },
    [layout.orientation, move, onSelect],
  )

  // Keep the selected node in view — the one piece of scrolling that survives,
  // because it follows the READER rather than pretending to follow work.
  useEffect(() => {
    const el = scrollRef.current
    const node = selectedId ? byId.get(selectedId) : null
    if (!el || !node) return
    // In layout units scaled to what is on screen (fitScale is 1 unless the
    // sheet was shrunk to fit — see below), or the target lands past the node.
    const top = (node.y + node.h / 2) * fitScale - el.clientHeight / 2
    const left = (node.x + node.w / 2) * fitScale - el.clientWidth / 2
    el.scrollTo({
      top: Math.max(0, Math.min(top, el.scrollHeight - el.clientHeight)),
      left: Math.max(0, Math.min(left, el.scrollWidth - el.clientWidth)),
      behavior: reduced ? 'auto' : 'smooth',
    })
    // byId intentionally excluded: re-running on a relayout would fight the
    // user's own scrolling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, reduced])

  if (!layout.nodes.length) return null

  const leadScoped = reachedIds instanceof Set && reachedIds.size > 0
  // Pills are bookends, never dimmed: every lead entered at Start.
  const onPath = (id) => !leadScoped || id === '__start__' || id === '__end__' || reachedIds.has(id)

  const startTone = passes.some((p) => p.status !== 'pending') ? 'live' : 'idle'
  const endTone = terminalDone ? 'live' : 'idle'
  const total = passes.length

  return (
    <div
      ref={scrollRef}
      role="group"
      aria-label="How this result was produced — select a step for its details"
      onClick={(e) => {
        // Clicking the paper clears the selection; clicks on a node stop here
        // only because the button handled them first.
        if (e.target === e.currentTarget) onSelect('')
      }}
      className="flow-grid relative h-full w-full overflow-auto rounded-[14px] [border:1px_solid_var(--border)]"
    >
      <div
        className="relative"
        style={{
          width: layout.width * fitScale,
          height: layout.height * fitScale,
          margin: '0 auto',
        }}
      >
      <div
        className="relative"
        style={{
          width: layout.width,
          height: layout.height,
          transform: fitScale !== 1 ? `scale(${fitScale})` : undefined,
          transformOrigin: '0 0',
        }}
      >
        {/* ── edges ───────────────────────────────────────────────────────
            Static. A settled run has no traffic on its wires, so there is no
            marching overlay: the tone alone says what each wire carried. In
            lead mode the lead's actual path is drawn heavier and everything
            else recedes. */}
        <svg
          className="pointer-events-none absolute inset-0"
          width={layout.width}
          height={layout.height}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          fill="none"
          aria-hidden="true"
        >
          {layout.edges.map((e) => {
            const tone = edgeTone(byId.get(e.source), byId.get(e.target))
            const walked = onPath(e.source) && onPath(e.target)
            const stroke = leadScoped && walked ? 'var(--primary)' : EDGE_STROKE[tone]
            const dashed = !walked && leadScoped ? true : tone === 'muted' || tone === 'dead'
            const opacity = leadScoped ? (walked ? 0.95 : 0.16) : EDGE_OPACITY[tone]
            return (
              <g key={e.id} opacity={opacity}>
                <path
                  d={e.d}
                  stroke={stroke}
                  strokeWidth={leadScoped && walked ? 2.25 : 1.75}
                  strokeLinecap="round"
                  strokeDasharray={dashed ? '4 5' : undefined}
                  fill="none"
                />
                <polygon points={e.arrow} fill={stroke} />
              </g>
            )
          })}
        </svg>

        {/* ── nodes ───────────────────────────────────────────────────────
            No band frame, no "N in parallel" caption: siblings sharing a row
            already say it. */}
        {layout.nodes.map((n) => {
          const walked = onPath(n.id)
          const step = n.order + 1
          const status = n.status || 'pending'
          const verdict = verdicts ? verdicts[n.id] : ''
          const label =
            n.kind === 'pill'
              ? ''
              : `Step ${step} of ${total}: ${n.title}. ${BADGE_TEXT[status] || BADGE_TEXT.pending}.` +
                (leadScoped
                  ? walked
                    ? ` This lead: ${verdict || 'passed through'}.`
                    : ' This lead never reached this step.'
                  : '')
          return (
            <div
              key={n.id}
              className="absolute left-0 top-0 transition-transform duration-[420ms] ease-[cubic-bezier(0.32,0.72,0.24,1)] motion-reduce:transition-none"
              style={{ width: n.w, height: n.h, transform: `translate3d(${n.x}px, ${n.y}px, 0)` }}
            >
              {n.kind === 'pill' ? (
                <PillNode
                  node={n}
                  label={n.id === '__start__' ? 'Start' : terminalLabel}
                  tone={n.id === '__start__' ? startTone : endTone}
                />
              ) : (
                <NodeRect
                  node={n}
                  selected={selectedId === n.id}
                  dimmed={leadScoped && !walked}
                  label={label}
                  onSelect={onSelect}
                  onKeyDown={keyHandler(n.id)}
                  buttonRef={(el) => {
                    if (el) btnRefs.current.set(n.id, el)
                    else btnRefs.current.delete(n.id)
                  }}
                />
              )}
            </div>
          )
        })}
      </div>
      </div>
    </div>
  )
}
